import { useEffect, useState } from 'react';
import type { User } from '@crmsy/shared';

/**
 * "View as" (D-36): an admin looks at the app from a teammate's perspective. Only what
 * depends on *who you are as a reader* changes: "Mine", the Follow-ups scope, "today" in
 * their time zone and the digest preview. Writes stay the admin's own (Actor, Actor Email).
 * Per device, survives a reload, cleared on sign-out.
 */
export interface Perspective {
  userId: string;
  name: string;
  notionUserId: string | null;
  timeZone: string;
}

const KEY = 'crmsy.viewAs';
let current: Perspective | null = read();
const listeners = new Set<() => void>();

function read(): Perspective | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Perspective) : null;
  } catch {
    return null;
  }
}

export function getViewAs(): Perspective | null {
  return current;
}

export function setViewAs(p: Perspective | null): void {
  current = p;
  try {
    if (p) localStorage.setItem(KEY, JSON.stringify(p));
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode */
  }
  for (const fn of listeners) fn();
}

export function useViewAs(): Perspective | null {
  const [v, set] = useState(current);
  useEffect(() => {
    const fn = () => set(current);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return v;
}

/** The reader's identity for filters and dates: the viewed teammate for an admin viewing as, else the user. */
export function usePerspective(user: User): Perspective & { viewing: boolean } {
  const v = useViewAs();
  if (v && user.role === 'admin' && v.userId !== user.id) return { ...v, viewing: true };
  return { userId: user.id, name: user.name, notionUserId: user.notionUserId, timeZone: user.timeZone, viewing: false };
}

/** Calendar date "today" for a perspective (the viewed person's time zone). */
export function todayFor(p: { timeZone: string }): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: p.timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}
